<template>
    <jet-dialog-modal 
        :show="modalOpen" 
        ref="modal"
        @close="closeModal"
    >
        <template #title>
            {{ isScheduledChange ? 'Schedule Plan Change' : 'Upcoming Invoice Preview' }}
        </template>

        <template #content>
            <!-- Scheduled Change Content -->
            <template v-if="isScheduledChange">
                <div class="mb-4 mt-4">
                    Your plan change will take effect at the end of your current billing cycle on <strong>{{ scheduleDate }}</strong>.<br/><br />
                    
                    <strong>What happens next:</strong><br/>
                    • You'll continue using your current plan until {{ scheduleDate }}<br/>
                    • No immediate charges or credits will be applied<br/>
                    • Your new plan will start on {{ scheduleDate }} and you'll be charged {{ newPlanPrice }}<br/>
                    • You can cancel this scheduled change anytime before then
                </div>
            </template>

            <!-- Invoice Preview Content -->
            <template v-else-if="invoice && invoice.hasOwnProperty('lines')">
                <div class="mb-4 mt-4">
                    <div v-for="line in invoice.lines.data" :key="line.id" class="mb-2">
                        <div class="flex justify-between">
                            <span v-if="line.proration">Prorated refund based on unused credits during current cycle</span>
                            <span v-else>{{ line.description }}</span>
                            <span>{{ formatMoney(line.amount) }}</span>
                        </div>
                    </div>
                    
                    <hr class="my-4"/>
                    
                    <div class="flex justify-between mb-2">
                        <span>Sub-total</span>
                        <span>{{ formatMoney(invoice.subtotal) }}</span>
                    </div>

                    <div class="flex justify-between mb-2" v-if="invoice.starting_balance">
                        <span class="text-gray-600">Starting Account Balance</span>
                        <span class="text-gray-600">{{ formatMoney(invoice.starting_balance) }}</span>
                    </div>
                    
                    <div class="flex justify-between mb-2 text-lg">
                        <strong>Amount Due Today</strong>
                        <strong>{{ formatMoney(invoice.amount_due) }}</strong>
                    </div>
                    
                    <div class="flex justify-between" v-if="invoice.ending_balance">
                        <span class="text-gray-600">Ending Account Balance</span>
                        <span class="text-gray-600">{{ formatMoney(invoice.ending_balance) }}</span>
                    </div>
                    
                    <p class="mt-6 italic text-base text-gray-600">
                        Confirming your account upgrade will reset your billing cycle to today's date.
                    </p>
                </div>
            </template>
        </template>

        <template #footer>
            <!-- Invoice Preview Footer -->
            <template v-if="!isScheduledChange">
                <jet-button text @click="closeModal" class="mr-4">
                    Cancel
                </jet-button>

                <jet-button secondary @click="submitHandler()" :disabled="upgrading">
                    <font-awesome-icon
                        :icon="['far', 'spinner']"
                        class="fa-spin mr-2"
                        v-show="upgrading"
                    />
                    CONFIRM ACCOUNT UPGRADE
                </jet-button>
            </template>
            
            <!-- Scheduled Change Footer -->
            <template v-else>
                <jet-button text @click="closeModal" class="mr-4">
                    Cancel
                </jet-button>

                <jet-button secondary @click="submitHandler()" :disabled="upgrading">
                    <font-awesome-icon
                        :icon="['far', 'spinner']"
                        class="fa-spin mr-2"
                        v-show="upgrading"
                    />
                    SCHEDULE PLAN CHANGE
                </jet-button>
            </template>
        </template>
    </jet-dialog-modal>
</template>

<script>
import JetDialogModal from '../Jetstream/DialogModal';
import JetSecondaryButton from '../Jetstream/SecondaryButton';
import JetButton from '../Jetstream/Button';
import axios from "axios";

export default {
    emits: ['close'],
    components: { JetDialogModal, JetSecondaryButton, JetButton },
    name: "PreviewInvoiceModal",
    props: {
        isOpen: {
            type: Boolean,
            default: false
        },
        invoice: {
            type: Object
        },
        licenseKey: {
            type: String,
            default: '',
        },
        isScheduledChange: {
            type: Boolean,
            default: false
        },
        scheduleDate: {
            type: String,
            default: ''
        },
        newPlanPrice: {
            type: String,
            default: ''
        }
    },
    data() {
        return {
            upgrading: false,
        }
    },
    methods: {
        formatMoney(amount) {
            // stripe returns data in cents so we convert to dollars
            // by dividing by 100
            const dollars = amount / 100;

            // Create our number formatter.
            const formatter = new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
            });

            return formatter.format(dollars);
        },
        closeModal() {
            this.$emit('close');
        },
        submitHandler() {
            let vm = this;
            vm.upgrading = true;
            axios.post(route('upgrade.json'), {
                lookup_key: vm.licenseKey
            })
                .then(function(response) {
                    if (vm.isScheduledChange) {
                        // For scheduled changes, show toast and stay on current page
                        const message = response.data.message || 'Your plan change has been scheduled.';
                        vm.$toasted.show(message, {
                            position: "bottom-right",
                            type: "success",
                            duration: 5000
                        });
                        vm.upgrading = false;
                        vm.$inertia.visit("/");
                    } else {
                        // For immediate upgrades, redirect to loading page
                        vm.upgrading = false;
                        window.location.href = route('stripe.loading') + '?context=upgrade';
                    }
                })
                .catch(function (error) {
                    vm.$toasted.show(`Something went wrong. Please try again.`, {
                        position: "bottom-right",
                        type: "error",
                        duration: 5000
                    });
                    vm.upgrading = false;
                });
        }
    },
    computed: {
        modalOpen() {
            return this.isOpen;
        }
    }

}
</script>

<style scoped>

</style>
